"use client";

/**
 * 앱 설치·열기 배너 — 화면 맨 위 한 줄.
 * @description 대표님 가이드 F2 의 `[스마트 배너] 앱 있으세요? 1탭으로 열기`. 필수로 잡혀
 *   있고 「§1.3 비대칭의 성립 조건」 이라 적혀 있다 — 이미 앱을 깐 사람이 링크를 타고
 *   왔을 때 한 번에 앱으로 넘어가야 웹과 앱이 서로를 먹여 살린다.
 *
 *   iOS Safari 는 `apple-itunes-app` 메타로 OS 가 직접 그려 준다(루트 레이아웃에 있다).
 *   문제는 그게 **Safari 에서만** 뜬다는 것이다. 정작 트래픽이 오는 인스타그램·X·카카오톡
 *   인앱 브라우저와 안드로이드에서는 아무것도 안 뜬다. 그래서 직접 그린다 — 대신 네이티브
 *   배너가 뜨는 자리(진짜 iOS Safari)에서는 우리 것을 접어 두 줄이 겹치지 않게 한다.
 *
 *   닫으면 기억한다. 가이드의 게이트 규칙과 같다 — `거절 상태 저장 · 재노출 없음`.
 *   거절 경로가 자연스러워야 거절률이라는 숫자가 정확해진다.
 *
 *   스토어로 보내는 일은 `openInApp` 이 이미 한다(앱을 열어 보고, 안 열리면 스토어).
 */
import Image from "next/image";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

import { trackEvent } from "@/shared/lib/analytics/amplitude";
import { getOS, openInApp } from "@/shared/lib/deepLink";
import { SITE_NAME } from "@/shared/config";

/** 한 번 닫으면 다시 묻지 않는다. */
const DISMISSED_KEY = "fw:app-banner-dismissed";

const ICON_SIZE = 36;

const COPY = {
  action: "Open",
  close: "Dismiss",
  defaultDescription: "Chat, check-in and updates",
} as const;

/**
 * iOS 네이티브 스마트 배너가 뜨는 환경인가.
 * @description 진짜 Safari 여야 한다. 크롬(CriOS)·파이어폭스(FxiOS)나 인앱 웹뷰
 *   (인스타그램·페이스북·카카오톡·라인)는 같은 WebKit 이라 UA 에 `Safari` 가 들어 있어도
 *   네이티브 배너를 그리지 않는다 — 거기서 우리 배너까지 접으면 아무것도 안 남는다.
 */
function hasNativeBanner(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (getOS() !== "ios") return false;
  if (/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)) return false;
  if (/FBAN|FBAV|Instagram|KAKAOTALK|Line|NAVER|Twitter|DaumApps/i.test(ua)) {
    return false;
  }
  return /Safari/i.test(ua);
}

interface AppInstallBannerProps {
  /** 앱에서 열 경로 — 지금 보고 있는 화면과 같은 곳으로 보낸다. */
  path: string;
  /** 어느 화면에서 떴는지. 계측에만 쓴다. */
  placement: string;
  description?: string;
}

export function AppInstallBanner({
  path,
  placement,
  description = COPY.defaultDescription,
}: AppInstallBannerProps) {
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    /*
     * 스토어가 없는 곳(데스크톱)에서는 낼 이유가 없다. 서버에서는 어느 쪽인지 알 수 없어
     * 붙은 뒤에 정한다 — 그래서 첫 렌더는 늘 비어 있다.
     */
    if (getOS() === "unknown" || hasNativeBanner()) return;
    if (window.localStorage.getItem(DISMISSED_KEY)) return;

    setVisible(true);
    trackEvent("App Banner Shown", { placement });
  }, [placement]);

  if (!isVisible) return null;

  return (
    <div className="flex shrink-0 items-center gap-[var(--fw-space-gap-sm)] bg-[var(--fw-color-surface-neutral-secondary)] px-[var(--fw-space-inset-screen)] py-[var(--fw-space-inset-sm)]">
      <button
        type="button"
        onClick={dismiss}
        aria-label={COPY.close}
        className="-ml-[var(--fw-space-inset-xxs)] shrink-0 cursor-pointer rounded-[var(--fw-radius-full)] p-[var(--fw-space-inset-xxs)] text-[var(--fw-color-content-neutral-tertiary)] transition-colors hover:bg-[var(--fw-color-surface-neutral-tertiary)]"
      >
        <X aria-hidden size={16} strokeWidth={2} />
      </button>

      <Image
        src="/assets/icons/icon-logo.png"
        alt=""
        aria-hidden
        width={ICON_SIZE}
        height={ICON_SIZE}
        className="shrink-0 rounded-[var(--fw-radius-md)]"
      />

      <span className="flex min-w-0 flex-1 flex-col">
        <span className="fw-l2 truncate text-[var(--fw-color-content-neutral-primary)]">
          {SITE_NAME}
        </span>
        <span className="fw-caption truncate text-[var(--fw-color-content-neutral-tertiary)]">
          {description}
        </span>
      </span>

      <button
        type="button"
        onClick={open}
        className="fw-l2 shrink-0 cursor-pointer rounded-[var(--fw-radius-full)] bg-[var(--fw-button-color-brand-surface-rest)] px-[var(--fw-space-inset-md)] py-[var(--fw-space-inset-xs)] text-[var(--fw-button-color-brand-content-rest)] transition-colors hover:bg-[var(--fw-button-color-brand-surface-hovered)]"
      >
        {COPY.action}
      </button>
    </div>
  );

  function dismiss() {
    window.localStorage.setItem(DISMISSED_KEY, "1");
    setVisible(false);
    trackEvent("App Banner Dismissed", { placement });
  }

  function open() {
    trackEvent("App Banner Opened", { placement });
    openInApp(path);
  }
}
